import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { confirmSwal } from "../utils/confirm";
import { auth } from "../firebase";

export default function AdminAnnouncementEditor() {
    const [announcements, setAnnouncements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openIndex, setOpenIndex] = useState(null);
    const [savingIndex, setSavingIndex] = useState(null);
    const [pages, setPages] = useState([]);

    useEffect(() => {
        const fetchAnnouncements = async () => {
            try {
                const token = await auth.currentUser.getIdToken();
                const res = await axios.get("/api/announcements", {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                setAnnouncements(res.data || []);
            } catch (error) {
                toast.error("Error fetching announcements");
            } finally {
                setLoading(false);
            }
        };
        fetchAnnouncements();
    }, []);

    useEffect(() => {
        const fetchPages = async () => {
            try {
                const token = await auth.currentUser.getIdToken();
                const res = await axios.get("/api/member-content", {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                setPages(res.data);
            } catch (error) {
                console.error("Error fetching pages:", error);
            }
        };
        fetchPages();
    }, []);

    const toggleOpen = (index) => {
        setOpenIndex(openIndex === index ? null : index);
    };

    const handleChange = (announcementIndex, field, value) => {
        const newAnnouncements = [...announcements];
        newAnnouncements[announcementIndex][field] = value;
        setAnnouncements(newAnnouncements);
    };

    const addAnnouncement = () => {
        const newAnnouncement = {
            _id: `new-${Date.now()}`,
            title: "",
            date: new Date().toISOString().split("T")[0],
            time: new Date().toTimeString().slice(0, 5),
            content: "",
            link: "",
        };
        setAnnouncements([newAnnouncement, ...announcements]);
        setOpenIndex(0);
    };

    const postAnnouncement = async (index) => {
        setSavingIndex(index);
        try {
            const token = await auth.currentUser.getIdToken();
            const updated = announcements[index];
            const announcementToSave = { ...updated };

            if (announcementToSave._id.startsWith("new-")) {
                const res = await axios.post("/api/announcements", announcementToSave, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                const newAnnouncements = [...announcements];
                newAnnouncements[index] = {
                    ...res.data
                };
                setAnnouncements(newAnnouncements);
                toast.success("Announcement posted!");
            } else {
                await axios.put(`/api/announcements/${updated._id}`, announcementToSave, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                toast.success("Announcement saved");
            }

            // Re-sort after saving
            setAnnouncements(prevAnnouncements => {
                return [...prevAnnouncements].sort((a, b) => {
                    const getDateTime = (obj) => {
                        if (!obj.date) return new Date(0);
                        const time = obj.time || "00:00";
                        return new Date(`${obj.date}T${time}`);
                    };
                    return getDateTime(b) - getDateTime(a);
                });
            });

        } catch {
            toast.error("Something went wrong");
        } finally {
            setSavingIndex(null);
        }
    };

    const deleteAnnouncement = async (index) => {
        const announcement = announcements[index];

        if (announcement._id.startsWith("new-")) {
            setAnnouncements(announcements.filter((_, i) => i !== index));
            if (openIndex === index) setOpenIndex(null);
        } else {
            const ok = await confirmSwal({
                title: `Delete announcement ${announcement.title}?`,
                text: "Once deleted it cannot be restored.",
                icon: "warning",
                confirmText: "Delete",
                confirmColor: "#d33",
            });
            if (!ok) return;
            try {
                const token = await auth.currentUser.getIdToken();
                await axios.delete(`/api/announcements/${announcement._id}`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                setAnnouncements(announcements.filter((_, i) => i !== index));
                if (openIndex === index) setOpenIndex(null);
                toast.info("Announcement deleted");
            } catch {
                toast.error("Something went wrong");
            }
        }
    };

    if (loading) return <p>Loading announcements...</p>;

    return (
        <div>
            <p>This is where you can post and edit announcements shown on the homepage of the Member Dashboard. If this list is empty, the announcements section will be hidden.</p>
            <button className="btn btn-admin mb-0" onClick={addAnnouncement}>+ Create Announcement</button>
            <div className="accordion mt-3" id="announcementsAccordian">
                {announcements.map((announcement, index) => {
                    return (
                        <div className="accordion-item" key={announcement._id}>
                            <h2 className="accordion-header" id={`heading-${index}`}>
                                <button
                                    className={`accordion-button ${openIndex === index ? "" : "collapsed"}`}
                                    type="button"
                                    onClick={() => toggleOpen(index)}
                                >
                                    <span><strong>{announcement.title || "(No Title)"}</strong></span>
                                </button>
                            </h2>

                            <div className={`accordion-collapse collapse ${openIndex === index ? "show" : ""}`}>
                                <div className="accordion-body">
                                    <div className="mb-2">
                                        <label className="form-label">Title</label>
                                        <input className="form-control" value={announcement.title} onChange={(e) => handleChange(index, "title", e.target.value)} />
                                    </div>

                                    <div className="mb-2">
                                        <label className="form-label">Date</label>
                                        <input type="date" className="form-control" value={announcement.date} disabled={true} />
                                    </div>

                                    <div className="mb-2">
                                        <label className="form-label">Time</label>
                                        <input type="time" className="form-control" value={announcement.time} disabled={true} />
                                    </div>

                                    <div className="mb-2">
                                        <label className="form-label">Content</label>
                                        <textarea className="form-control" value={announcement.content} onChange={(e) => handleChange(index, "content", e.target.value)} />
                                    </div>

                                    <div className="mb-2">
                                        <label className="form-label">Read More Link <small className="text-body-secondary">(Optional)</small></label>
                                        <select
                                            className="form-select"
                                            value={announcement.link}
                                            onChange={(e) => handleChange(index, "link", e.target.value)}
                                        >
                                            <option value="">(No link)</option>
                                            {pages.map((p) => (
                                                <option key={p.slug} value={p.slug}>
                                                    {p.title}
                                                </option>
                                            ))}
                                        </select>
                                    </div>

                                    <button className="btn btn-admin me-2" onClick={() => postAnnouncement(index)} disabled={savingIndex === index}>
                                        {savingIndex === index ? `${announcement._id.startsWith("new-") ? "Posting..." : "Saving..."}` : `${announcement._id.startsWith("new-") ? "Post" : "Save Changes"}`}
                                    </button>
                                    <button className="btn btn-outline-secondary" onClick={() => deleteAnnouncement(index)} disabled={savingIndex === index}>
                                        {announcement._id.startsWith("new-") ? "Cancel" : "Delete"}
                                    </button>

                                </div>
                            </div>

                        </div>
                    );
                })}
            </div>
        </div>
    );
}